import { Fragment, useState } from "react";
import styled from "styled-components";
import { Field } from "formik";

import AnimatedButton from "../../components/AnimatedButton";
import ConfirmationDialog from "../../components/ConfirmationDialog";

import { Title } from "../../styled";

const Container = styled.div.attrs({
  className: "pa3 pa4-m pv5-l ph6-l",
})`
  @media screen and (min-width: 70em) {
    background-image: url("/img/checklist.svg");
    z-index: -1;
    background-repeat: no-repeat;
    background-size: 40% 40%;
    background-position: 410px 85px;
  }
`;

const Label = styled.label.attrs({
  className: "f6 fw5 db mb3 mt3 lh-copy",
})``;

const OptionContainer = styled.label`
  display: flex;
  align-items: flex-start;
  margin: 1rem 0;
  padding: 1rem;
  border: 2px solid ${props => props.selected ? "#000" : "#e0e0e0"};
  border-radius: 4px;
  cursor: pointer;
  background-color: ${props => props.selected ? "#f5f5f5" : "#fff"};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.selected ? "#000" : "#999"};
    background-color: ${props => props.selected ? "#f5f5f5" : "#fafafa"};
  }
`;

const RadioButton = styled.input`
  margin-right: 0.75rem;
  margin-top: 0.125rem;
  cursor: pointer;
  flex-shrink: 0;
`;

const OptionContent = styled.div`
  flex: 1;
`;

const MainText = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #000;
  margin-bottom: 0.25rem;
`;

const SubText = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.5rem;
`;

export function Headline() {
  return (
    <Container>
      <Title>
        Let's check your <br /> eligibility
      </Title>
    </Container>
  );
}

export function ApplicationForm(props) {
  const [isChanged, setChanged] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    stepForward,
    stepBack,
    stepForwardLoading,
    formField: { eligibilityQuestion },
    setFieldValue,
    values,
  } = props;

  // Flag to enable/disable confirmation dialog
  // Set to true to re-enable the confirmation dialog
  const ENABLE_CONFIRMATION_DIALOG = false;

  const currentValue = values?.[eligibilityQuestion.name];
  const hasHadClaims = currentValue === "Yes";

  const handleSelectNoClaims = (e) => {
    e?.preventDefault?.();
    setFieldValue(eligibilityQuestion.name, "No");
    setChanged(true);
  };

  const handleSelectHasClaims = (e) => {
    e?.preventDefault?.();
    if (ENABLE_CONFIRMATION_DIALOG) {
      // Show confirmation dialog instead of immediately setting value
      setShowConfirmation(true);
    } else {
      // Directly set the value without confirmation dialog
      setFieldValue(eligibilityQuestion.name, "Yes");
      setChanged(true);
    }
  };

  // Confirmation dialog handlers (kept for easy re-enabling)
  const handleConfirmHasClaims = () => {
    setFieldValue(eligibilityQuestion.name, "Yes");
    setChanged(true);
    setShowConfirmation(false);
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <Fragment>
      <div>
        <Label htmlFor={eligibilityQuestion.name}>
          {eligibilityQuestion.label}
        </Label>
        
        {/* Hidden field to ensure Formik tracks the value */}
        <Field
          type="hidden"
          name={eligibilityQuestion.name}
        />

        {/* Option 1: I affirm I have NOT had more than one Workers' Comp claim */}
        <OptionContainer
          selected={currentValue === "No"}
          onClick={handleSelectNoClaims}
        >
          <RadioButton
            type="radio"
            name={eligibilityQuestion.name}
            value="No"
            checked={currentValue === "No"}
            onChange={handleSelectNoClaims}
            readOnly
          />
          <OptionContent>
            <MainText>
              I affirm I have NOT had more than one Workers' Comp claim in the last 3 years
            </MainText>
          </OptionContent>
        </OptionContainer>

        {/* Option 2: I have had more than one Workers' Comp claim */}
        <OptionContainer
          selected={currentValue === "Yes"}
          onClick={handleSelectHasClaims}
        >
          <RadioButton
            type="radio"
            name={eligibilityQuestion.name}
            value="Yes"
            checked={currentValue === "Yes"}
            onChange={handleSelectHasClaims}
            readOnly
          />
          <OptionContent>
            <MainText>
              I have had more than one Workers' Comp claim in the last 3 years
            </MainText>
            <SubText>
              Selecting this option will require manual review of your application
            </SubText>
          </OptionContent>
        </OptionContainer>
      </div>

      {/* Confirmation dialog - disabled but kept for easy re-enabling */}
      {ENABLE_CONFIRMATION_DIALOG && (
        <ConfirmationDialog
          isOpen={showConfirmation}
          onConfirm={handleConfirmHasClaims}
          onCancel={handleCancelConfirmation}
          title="Confirm Your Selection"
          message="You've indicated that you have had more than one Workers' Comp claim in the last 3 years. This will require manual review of your application, and we'll follow up with you within 24 hours. Are you sure you want to continue?"
        />
      )}

      <div className="mt3 fr">
        <AnimatedButton onClick={stepBack} customStyle={"mr2"}>
          Back
        </AnimatedButton>
        <AnimatedButton
          onClick={stepForward}
          isSubmitting={stepForwardLoading}
          isDisabled={!isChanged}
          primary
        >
          Continue
        </AnimatedButton>
      </div>
    </Fragment>
  );
}
